// src/components/maps/admin/FloorPlanUploader.jsx
import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import indoorMappingService from '../../../services/indoorMappingService';

/**
 * Floor plan PDF uploader component
 * Handles drag-drop upload and displays progress
 */
const FloorPlanUploader = ({ locationId, onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [generatedFloors, setGeneratedFloors] = useState([]);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];

      // Validate file type
      if (file.type !== 'application/pdf') {
        setError('Please upload a PDF file');
        return;
      }

      try {
        setUploading(true);
        setError(null);
        setSuccess(false);
        setProgress(0);

        const result = await indoorMappingService.uploadFloorPlanPdf(
          locationId,
          file,
          (progressValue) => {
            setProgress(progressValue);
          }
        );

        setGeneratedFloors(result.floors || []);
        setSuccess(true);
        setUploading(false);

        if (onUploadComplete) {
          onUploadComplete(result);
        }
      } catch (err) {
        setError(err.message || 'Failed to upload floor plan');
        setUploading(false);
      }
    },
    [locationId, onUploadComplete]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    multiple: false,
    disabled: uploading,
  });

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Upload Floor Plan PDF
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a PDF file containing floor plan images. Each page will be converted to a
        separate floor.
      </Typography>

      {/* Dropzone */}
      <Box
        {...getRootProps()}
        sx={{
          border: 2,
          borderStyle: 'dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: uploading ? 'default' : 'pointer',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: uploading ? 'grey.300' : 'primary.main',
            backgroundColor: uploading ? 'background.paper' : 'action.hover',
          },
        }}
      >
        <input {...getInputProps()} />

        <CloudUploadIcon
          sx={{
            fontSize: 64,
            color: isDragActive ? 'primary.main' : 'text.secondary',
            mb: 2,
          }}
        />

        {isDragActive ? (
          <Typography variant="body1">Drop the PDF file here...</Typography>
        ) : (
          <>
            <Typography variant="body1" gutterBottom>
              Drag and drop a PDF file here, or click to select
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Supported format: PDF
            </Typography>
          </>
        )}
      </Box>

      {/* Upload Progress */}
      {uploading && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" gutterBottom>
            Uploading... {progress}%
          </Typography>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}

      {/* Success Message and Generated Floors */}
      {success && (
        <>
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 3 }}>
            Floor plan uploaded successfully! Generated {generatedFloors.length} floor(s).
          </Alert>

          {generatedFloors.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Generated Floors:
              </Typography>
              <List dense>
                {generatedFloors.map((floor, index) => (
                  <ListItem key={floor.id}>
                    <ListItemIcon>
                      <DescriptionIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={floor.name || `Floor ${index + 1}`}
                      secondary={`Level ${floor.level}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default FloorPlanUploader;
